import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle2, X, Loader2 } from 'lucide-react';

// Shown after a client chooses "Maksāt online": the slot is held for a short
// window while they pay. A live countdown mirrors the server-side hold so the
// client knows exactly how long their time is reserved.
export default function PaymentHoldOverlay({ url, until, onClose }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const remaining = Math.max(0, Math.floor((until - now) / 1000));
  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');
  const expired = remaining <= 0;
  const [going, setGoing] = useState(false);

  // When the hold runs out, don't strand the client on a dead screen —
  // show the "expired" panel briefly, then auto-close back to the booking page.
  useEffect(() => {
    if (!expired) return;
    const t = setTimeout(() => onClose?.(), 4000);
    return () => clearTimeout(t);
  }, [expired, onClose]);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      data-testid="payment-hold-overlay">
      <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#111] p-7 text-center"
        style={{ fontFamily: 'Manrope, sans-serif' }}>
        {!expired ? (
          <>
            <div className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-amber-500/15 flex items-center justify-center">
              <Clock className="w-8 h-8 text-amber-400" />
            </div>
            <p className="text-white/60 text-sm">Tavs laiks ir rezervēts vēl</p>
            <p className="text-5xl font-extrabold text-white tabular-nums my-2 tracking-tight" data-testid="payment-hold-countdown">
              {mm}:{ss}
            </p>
            <p className="text-white/50 text-sm mb-6">Pabeidz apmaksu, lai apstiprinātu rezervāciju. Ja laiks beigsies, rezervācija tiks atcelta un laiks atbrīvots.</p>
            <button
              onClick={() => { setGoing(true); window.location.href = url; }}
              disabled={going}
              className="w-full h-12 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
              data-testid="payment-hold-pay-btn"
            >
              {going ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
              Pāriet uz drošu apmaksu
            </button>
            <button onClick={onClose} className="mt-3 text-white/40 hover:text-white/70 text-sm transition-colors" data-testid="payment-hold-cancel-btn">
              Atcelt
            </button>
          </>
        ) : (
          <>
            <div className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center">
              <X className="w-8 h-8 text-white/60" />
            </div>
            <p className="text-white font-semibold text-lg mb-1">Rezervētais laiks beidzies</p>
            <p className="text-white/50 text-sm mb-6">Apmaksa netika pabeigta laikā un laiks atbrīvots. Šis logs aizvērsies automātiski — vari arī izvēlēties laiku no jauna.</p>
            <button onClick={onClose} className="w-full h-12 rounded-full bg-white/10 hover:bg-white/15 text-white font-semibold transition-colors" data-testid="payment-hold-retry-btn">
              Izvēlēties no jauna
            </button>
          </>
        )}
      </div>
    </div>
  );
}
